import React, { useRef, useState } from 'react';
import { useMedia } from 'react-use';

import { getAmplitudeClient } from '@/features/amplitude';
import { Icon, PageHeader } from '@/features/common';
import { usePlanLimits } from '@/hooks/planLimits';
import { useWorkspaceId } from '@/hooks/router';
import { useGetCareersPage } from '@/queries/careers/queries';
import styled from '@emotion/styled';
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro';
import { DSButton } from '@hundred5/design-system';
import { usePostHog } from 'posthog-js/react';
import { useFreeTrial } from '../billing';
import { useClickOutside } from '../common/hooks/use-click-outside';
import { useGenericPricingUpsellModalContext } from '../upsell';
import { getHostnameFromApiUrl } from './components/careers-job-opening-list/careers-job-opening-list.utils';
import { ShareCareersModal } from './components/share-careers-modal';

interface CareersHeaderProps {
  onSave?: () => void;
  saveDisabled: boolean;
}

export function CareersHeader(props: CareersHeaderProps) {
  const isMobile = useMedia('(max-width: 768px)');
  const [open, setOpen] = useState(false);
  const ref = useRef<null | HTMLDivElement>(null);
  const workspaceId = useWorkspaceId();
  const query = useGetCareersPage(Number(workspaceId));
  const isPublished = query.data?.published ?? false;
  const amplitude = getAmplitudeClient();
  const posthog = usePostHog();
  useClickOutside(ref, () => setOpen(false));
  const apiUrl = import.meta.env.VITE_API_URL;
  const url = query.data?.subdomain
    ? `https://${query.data?.subdomain}.${getHostnameFromApiUrl(apiUrl)}`
    : '';
  const { isTrialActive } = useFreeTrial();
  const canPublishCareersPage = !!usePlanLimits()('careers');
  const { openPricingUpsell } = useGenericPricingUpsellModalContext();

  return (
    <PageHeader
      title="Careers page"
      sticky={isMobile}
      actions={
        <ButtonContainer ref={ref}>
          {query.data?.subdomain && query.data?.published && (
            <DSButton
              variant="secondary"
              Icon={<Icon icon={regular('arrow-up-right-from-square')} />}
              data-rh="Open page"
              data-rh-at="left"
              onClick={() => {
                window.open(url, '_blank');
              }}
            />
          )}
          <DSButton
            variant="secondary"
            form="careers-settings"
            type="submit"
            onClick={props.onSave}
            disabled={props.saveDisabled}
          >
            Save changes
          </DSButton>
          <DSButton
            Icon={
              isPublished ? <Icon icon={regular('share-nodes')} /> : undefined
            }
            onClick={() => {
              if (isTrialActive || !canPublishCareersPage) {
                openPricingUpsell('careers', 'premium');
              } else {
                setOpen(true);
                amplitude?.logEvent('careers/share', {
                  'workspace id': workspaceId,
                });
                posthog?.capture('hire_admin_app:careers/share', {
                  'workspace id': workspaceId,
                });
              }
            }}
          >
            {isPublished ? 'Share' : 'Publish'}
          </DSButton>
          <ShareCareersModal open={open} onClose={() => setOpen(false)} />
        </ButtonContainer>
      }
    />
  );
}

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;
